import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Project, projectApi } from '../services/api';
import { useAuth } from './useAuth';
import toast from 'react-hot-toast';

interface ProjectContextType {
  selectedProject: Project | null;
  projects: Project[];
  loading: boolean;
  error: string | null;
  setSelectedProject: (project: Project | null) => void;
  refreshProjects: () => Promise<void>;
  isProjectSelected: boolean;
  getProjectById: (id: string) => Project | undefined;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated, activeOrganization } = useAuth();

  // Load projects on mount and when user or active organization changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // Clear selected project when org changes to avoid stale selection
      setSelectedProject(null);
      localStorage.removeItem('selectedProjectId');
      loadProjects();
    } else {
      // Clear projects when user logs out
      setProjects([]);
      setSelectedProject(null);
      setLoading(false);
    }
  }, [isAuthenticated, user, activeOrganization]);

  // Load selected project from localStorage when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      const savedProjectId = localStorage.getItem('selectedProjectId');
      if (savedProjectId) {
        const savedProject = projects.find(p => p._id === savedProjectId);
        if (savedProject) {
          setSelectedProjectState(savedProject);
        } else {
          // If saved project not found, clear from localStorage and select first active project
          localStorage.removeItem('selectedProjectId');
          selectDefaultProject();
        }
      } else {
        // No saved project, select first active project
        selectDefaultProject();
      }
    }
  }, [projects, selectedProject]);

  const selectDefaultProject = () => {
    if (projects.length > 0) {
      // Prefer active projects, then any project
      const activeProject = projects.find(p => p.status === 'active');
      const defaultProject = activeProject || projects[0];
      
      setSelectedProjectState(defaultProject);
      localStorage.setItem('selectedProjectId', defaultProject._id);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await projectApi.getAll({ 
        limit: 100, // Get all projects user has access to
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      });
      
      if (response.data.success) {
        const projectList = response.data.data.projects || [];
        setProjects(projectList);
        
        // If no projects exist, show a helpful message
        if (projectList.length === 0) {
          setError('No projects found. Create a project to get started with audio features.');
        }
      } else {
        throw new Error('Failed to load projects');
      }
    } catch (err: any) {
      console.error('Failed to load projects:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load projects';
      setError(errorMessage);
      
      // Only show toast for actual errors, not when no projects exist
      if (err.response?.status !== 404) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const setSelectedProject = (project: Project | null) => {
    setSelectedProjectState(project);
    if (project) {
      localStorage.setItem('selectedProjectId', project._id);
      toast.success(`Switched to project: ${project.name}`);
    } else {
      localStorage.removeItem('selectedProjectId');
    }
  };

  const refreshProjects = async () => {
    await loadProjects();
  };

  const getProjectById = (id: string): Project | undefined => {
    return projects.find(p => p._id === id);
  };

  const value: ProjectContextType = {
    selectedProject,
    projects,
    loading,
    error,
    setSelectedProject,
    refreshProjects,
    isProjectSelected: selectedProject !== null,
    getProjectById,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export default ProjectContext;